const { useState, useEffect } = wp.element;
import { Modal } from '@wordpress/components';
const { Button } = wp.components;
const { __ } = wp.i18n;

const TinyMCEEditor = ({
  value,
  onChange,
  placeholder = __('Click to edit...', 'pg'),
  className = 'description',
  editorId = 'gutenberg-tinymce-editor',
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editorContent, setEditorContent] = useState(value);
  const [editorCssUrl, setEditorCssUrl] = useState(null);

  const fetchEditorCssUrl = async () => {
    try {
      const response = await fetch(
        '/wp-content/themes/positiongreen/public/manifest.json'
      );
      const manifest = await response.json();
      if (manifest['editor.css']) {
        setEditorCssUrl(
          `/wp-content/themes/positiongreen/public/${manifest['editor.css']}`
        );
      }
    } catch (error) {
      console.error('Error loading editor.css from manifest:', error);
    }
  };

  useEffect(() => {
    fetchEditorCssUrl();
  }, []);

  useEffect(() => {
    if (isEditing && typeof window.tinymce !== 'undefined') {
      setTimeout(() => {
        // Destroy any existing instance before reinitializing
        if (window.tinymce.get(editorId)) {
          window.tinymce.get(editorId).remove();
        }

        // Initialize TinyMCE
        window.tinymce.init({
          selector: `#${editorId}`,
          skin: false,
          content_css: [editorCssUrl],
          height: 300,
          menubar: false,
          branding: false,
          plugins: 'lists link',
          toolbar:
            'bold italic | liststyles bullist numlist | link | styleselect',
          formats: {
            alignleft: { selector: 'p, div, ul, ol', classes: 'align-left' },
            aligncenter: {
              selector: 'p, div, ul, ol',
              classes: 'align-center',
            },
            alignright: { selector: 'p, div, ul, ol', classes: 'align-right' },
            removeformat: [
              { selector: 'ul, ol', attributes: ['class'] }, // Ensures only one class at a time
            ],
          },
          setup: (editor) => {
            editor.on('init', () => {
              editor.setContent(editorContent);
            });

            editor.on('keyup change', () => {
              setEditorContent(editor.getContent());
            });

            // Handle applying only one class at a time
            editor.addButton('liststyles', {
              type: 'menubutton',
              text: 'List Styles',
              menu: [
                {
                  text: 'Bulleted List',
                  onclick: () => applySingleClass(editor, 'pg-list--disc'),
                },
                {
                  text: 'Alpha List',
                  onclick: () => applySingleClass(editor, 'pg-list--alpha'),
                },
                {
                  text: 'Checklist List',
                  onclick: () => applySingleClass(editor, 'pg-list--checklist'),
                },
                {
                  text: 'Numeric List',
                  onclick: () => applySingleClass(editor, 'pg-list--numeric'),
                },
              ],
            });

            function applySingleClass(editor, newClass) {
              let node = editor.selection.getNode();

              // Find the first UL or OL inside the selected element
              let listElement = node.querySelector?.('ul, ol');

              // If not found, find the closest UL or OL ancestor
              if (!listElement) {
                listElement = node.closest?.('ul, ol');
              }

              // If still no list element, exit function
              if (!listElement) {
                return;
              }

              let allowedClasses = [
                'pg-list--disc',
                'pg-list--alpha',
                'pg-list--checklist',
                'pg-list--numeric',
              ];

              // Remove all previous allowed classes before applying the new one
              allowedClasses.forEach((cls) => {
                listElement.classList.remove(cls);
              });

              // Apply only the selected class
              listElement.classList.add(newClass);
            }
          },
        });
      }, 100);
    }
  }, [isEditing]);

  // Ensure TinyMCE is destroyed when modal closes
  const handleClose = () => {
    if (window.tinymce.get(editorId)) {
      window.tinymce.get(editorId).remove();
    }
    setIsEditing(false);
  };

  return (
    <>
      {/* Click to Open Modal */}
      <div
        className="description-preview"
        onClick={() => setIsEditing(true)}
        style={{
          cursor: 'pointer',
          padding: '0',
          border: 'none',
          background: 'transparent',
          minHeight: '2rem',
        }}
      >
        {value && value.trim() !== '' ? (
          <div
            className={className}
            dangerouslySetInnerHTML={{ __html: value }}
          />
        ) : (
          <span className="placeholder">{placeholder}</span>
        )}
      </div>

      {/* TinyMCE Editor in Modal */}
      {isEditing && (
        <Modal
          title={__('Edit Description', 'pg')}
          onRequestClose={handleClose}
          className="tinymce-modal"
        >
          <textarea id={editorId} defaultValue={editorContent}></textarea>

          <div
            style={{
              marginTop: '15px',
              paddingTop: '10px',
              borderTop: '1px solid #ddd',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px',
            }}
          >
            <Button isSecondary onClick={handleClose}>
              {__('Cancel', 'pg')}
            </Button>
            <Button
              isPrimary
              onClick={() => {
                const editorInstance = window.tinymce.get(editorId);
                if (editorInstance) {
                  onChange(editorInstance.getContent()); // Get fresh content directly
                }
                handleClose();
              }}
            >
              {__('Save', 'pg')}
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
};

export default TinyMCEEditor;
